import React, { useEffect, useState } from 'react';
import {
  Alert,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  TextField,
} from '@mui/material';
import { DecisionType, readableError } from './reviewModel';
import { formatRevisionLimit } from '../../utils/projectStatus';

interface RevisionDecisionDialogProps {
  open: boolean;
  type: DecisionType;
  /** Version being decided (v2 → 2). */
  version: number;
  /** The version the editor would upload next. */
  nextVersion: number;
  roundsUsed: number;
  /** The project's change-round allowance (Infinity = no limit). */
  roundsLimit: number;
  editorName?: string | null;
  onClose: () => void;
  /** Rejects to keep the dialog open with the error shown. */
  onConfirm: (comments: string) => Promise<void>;
}

/**
 * The one confirmation for a creator's decision on a revision: "Approve" or
 * "Request changes", with the same words wherever it is opened from.
 *
 * The change-round limit is enforced by the server, as before: when the
 * rounds are used up the dialog says so up front, and the server's refusal
 * is shown here if the creator sends the request anyway.
 */
const RevisionDecisionDialog: React.FC<RevisionDecisionDialogProps> = ({
  open,
  type,
  version,
  nextVersion,
  roundsUsed,
  roundsLimit,
  editorName,
  onClose,
  onConfirm,
}) => {
  const [comments, setComments] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (open) {
      setComments('');
      setError(null);
      setSubmitting(false);
    }
  }, [open, type, version]);

  const approve = type === 'approve';
  const editor = editorName?.trim() || '';
  const roundsLeft = Number.isFinite(roundsLimit) ? Math.max(0, roundsLimit - roundsUsed) : Infinity;
  const outOfRounds = !approve && roundsLeft === 0;
  const canSubmit = approve || comments.trim().length > 0;

  const handleClose = () => {
    if (!submitting) onClose();
  };

  const handleConfirm = async () => {
    if (submitting || !canSubmit) return;
    setSubmitting(true);
    setError(null);
    try {
      await onConfirm(comments.trim());
    } catch (confirmError) {
      setError(readableError(confirmError, 'Your decision wasn’t saved. Please try again.'));
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ fontWeight: 700 }}>
        {approve ? `Approve v${version}?` : `Request changes on v${version}`}
      </DialogTitle>
      <DialogContent>
        {outOfRounds ? (
          <Alert severity="warning" sx={{ mb: 2 }}>
            You’ve used all {formatRevisionLimit(roundsLimit)} change rounds on this project, so this request will be
            declined. Approve this version, or send your editor a message instead.
          </Alert>
        ) : (
          <DialogContentText sx={{ mb: 2 }}>
            {approve
              ? `v${version} becomes the final video for this project, and ${editor || 'your editor'} is notified. You can close the project afterwards.`
              : `${editor || 'Your editor'} is notified and can upload v${nextVersion}.${
                  Number.isFinite(roundsLimit) ? ` This uses change round ${roundsUsed + 1} of ${formatRevisionLimit(roundsLimit)}.` : ''
                }`}
          </DialogContentText>
        )}
        <TextField
          fullWidth
          multiline
          minRows={approve ? 3 : 4}
          label={approve ? 'Comments for your editor (optional)' : 'What needs to change'}
          placeholder={approve ? undefined : 'Be specific. Timecodes help, for example 00:01:12.'}
          value={comments}
          onChange={(event) => setComments(event.target.value)}
          required={!approve}
          disabled={submitting}
          autoFocus
        />
        {error && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {error}
          </Alert>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2.5 }}>
        <Button onClick={handleClose} disabled={submitting}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={() => void handleConfirm()}
          disabled={submitting || !canSubmit}
          startIcon={submitting ? <CircularProgress size={16} color="inherit" /> : undefined}
        >
          {submitting ? 'Saving…' : approve ? `Approve v${version}` : 'Request changes'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default RevisionDecisionDialog;
